import React, { useCallback } from 'react'
import { useThemeContext } from '@sketch/global-styles'
import { useDebounceValue } from '@sketch/utils'
import {
  Section,
  Title,
  Description,
} from '../WorkspaceSettingsProfile/WorkspaceSettingsProfile.styles'
import {
  useExportWorkspaceAuditLogsMutation,
  useGetWorkspaceAuditLogQuery,
} from '@sketch/gql-types'
import { WorkspaceAuditLogActionFilter } from './WorkspaceAuditLogActionFilter'
import { WorkspaceAuditLogTable } from './WorkspaceAuditLogTable'
import { downloadFromUrl } from '../../utils'
import {
  DateFilterLabel,
  DatePicker,
  FilterBar,
} from './WorkspaceSettingsAuditLog.styles'
import type { WorkspaceAuditAction } from './types'
import { visibleAuditActions } from './visibleAuditActions'
import {
  Button,
  ErrorMessage,
  InfiniteList,
  LoadingState,
} from '@sketch/components'

export interface WorkspaceSettingsAuditLogProps {
  workspaceIdentifier: string
}

const LOGS_PER_PAGE = 25
const DATE_FILTER_DEBOUNCE_MS = 300

interface AuditLogFilters {
  requestedActions: WorkspaceAuditAction[]
  startDate?: string
  endDate?: string
}

const AuditLogResults = ({
  workspaceIdentifier,
  requestedActions,
  startDate,
  endDate,
}: WorkspaceSettingsAuditLogProps & AuditLogFilters) => {
  const { data, loading, error, refetch, fetchMore } =
    useGetWorkspaceAuditLogQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        workspaceIdentifier,
        limit: LOGS_PER_PAGE,
        actionFilters: requestedActions,
        startDate,
        endDate,
      },
    })

  const after = data?.workspaceAuditLogs.meta.after ?? null
  const items = data?.workspaceAuditLogs.entries || []
  const handleLoadMore = useCallback(() => {
    if (!after) {
      return Promise.resolve()
    }

    return fetchMore({
      variables: {
        workspaceIdentifier,
        limit: LOGS_PER_PAGE,
        actionFilters: requestedActions,
        startDate,
        endDate,
        after,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.workspaceAuditLogs) {
          return previousResult
        }

        return {
          ...fetchMoreResult,
          workspaceAuditLogs: {
            ...fetchMoreResult.workspaceAuditLogs,
            entries: [
              ...(previousResult.workspaceAuditLogs?.entries ?? []),
              ...(fetchMoreResult.workspaceAuditLogs.entries ?? []),
            ],
          },
        }
      },
    })
  }, [
    requestedActions,
    startDate,
    endDate,
    after,
    fetchMore,
    workspaceIdentifier,
  ])

  if (error) {
    return (
      <ErrorMessage.Generic
        title="Could not load the audit log"
        extra={
          <Button onClick={() => refetch().catch(() => {})}>Try again</Button>
        }
      />
    )
  }

  return (
    <InfiniteList
      canLoadMore={after !== null}
      onLoadMore={handleLoadMore}
      renderLoading={() => <LoadingState />}
    >
      <WorkspaceAuditLogTable items={items} isLoading={loading} />
    </InfiniteList>
  )
}

const ExportButton = ({
  workspaceIdentifier,
  requestedActions,
  startDate,
  endDate,
  disabled,
}: WorkspaceSettingsAuditLogProps &
  AuditLogFilters & { disabled: boolean }) => {
  const [exportAuditLogs, { loading }] = useExportWorkspaceAuditLogsMutation({
    onError: 'show-toast',
    onCompleted: ({ workspaceAuditLogsExport }) => {
      downloadFromUrl(workspaceAuditLogsExport.downloadUrl)
    },
  })

  return (
    <Button
      variant="secondary"
      loading={loading}
      disabled={disabled}
      onClick={() =>
        exportAuditLogs({
          variables: {
            workspaceIdentifier,
            search: { action: requestedActions, startDate, endDate },
          },
        })
      }
    >
      Download CSV
    </Button>
  )
}

export const WorkspaceSettingsAuditLog: React.FC<
  WorkspaceSettingsAuditLogProps
> = ({ workspaceIdentifier }) => {
  const { isDarkMode } = useThemeContext()
  const [actionFilters, setActionFilters] = React.useState<
    WorkspaceAuditAction[] | undefined
  >(undefined)
  const [startDate, setStartDate] = React.useState<string | undefined>()
  const [endDate, setEndDate] = React.useState<string | undefined>()
  const dateRange = React.useMemo(
    () => ({ startDate, endDate }),
    [startDate, endDate]
  )
  const appliedDateRange = useDebounceValue(dateRange, DATE_FILTER_DEBOUNCE_MS)
  const datesPending =
    startDate !== appliedDateRange.startDate ||
    endDate !== appliedDateRange.endDate
  const today = new Date().toISOString().slice(0, 10)
  const dateError =
    (startDate && startDate > today) || (endDate && endDate > today)
      ? 'Dates cannot be in the future.'
      : startDate && endDate && startDate > endDate
        ? 'Start date must be on or before end date.'
        : undefined

  const requestedActions = actionFilters?.length
    ? actionFilters
    : visibleAuditActions

  return (
    <>
      <Section>
        <Title>Audit Log</Title>
        <Description>
          The audit log lets Workspace admins review actions, including who did
          what and when.
        </Description>
      </Section>

      <FilterBar alignItems="center" mb={18}>
        <WorkspaceAuditLogActionFilter
          value={actionFilters}
          onChange={setActionFilters}
        />
        <DateFilterLabel>
          From
          <DatePicker
            aria-invalid={!!dateError}
            aria-describedby={dateError ? 'audit-log-date-error' : undefined}
            isDarkMode={!!isDarkMode}
            value={startDate ?? ''}
            max={endDate && endDate < today ? endDate : today}
            onChange={event => setStartDate(event.target.value || undefined)}
          />
        </DateFilterLabel>
        <DateFilterLabel>
          To
          <DatePicker
            aria-invalid={!!dateError}
            aria-describedby={dateError ? 'audit-log-date-error' : undefined}
            isDarkMode={!!isDarkMode}
            value={endDate ?? ''}
            min={startDate}
            max={today}
            onChange={event => setEndDate(event.target.value || undefined)}
          />
        </DateFilterLabel>
        <ExportButton
          workspaceIdentifier={workspaceIdentifier}
          requestedActions={requestedActions}
          startDate={appliedDateRange.startDate}
          endDate={appliedDateRange.endDate}
          disabled={!!dateError || datesPending}
        />
      </FilterBar>
      <Section
        id={dateError ? 'audit-log-date-error' : undefined}
        role={dateError ? 'alert' : undefined}
      >
        {dateError ? (
          <ErrorMessage.Generic
            icon={null}
            title="Invalid date range"
            description={dateError}
          />
        ) : datesPending ? (
          <LoadingState />
        ) : (
          // Keep pending pagination requests tied to their original query.
          <AuditLogResults
            key={JSON.stringify([
              workspaceIdentifier,
              requestedActions,
              appliedDateRange.startDate,
              appliedDateRange.endDate,
            ])}
            workspaceIdentifier={workspaceIdentifier}
            requestedActions={requestedActions}
            startDate={appliedDateRange.startDate}
            endDate={appliedDateRange.endDate}
          />
        )}
      </Section>
    </>
  )
}
